import React from 'react';
import { cn } from '@/lib/utils';
import { formatCents, formatCount } from '@/lib/slotPricing';
import Button from '../Button';
import Heading from '../Heading';
import SiteImage from '../SiteImage';
import TierCoaster from './TierCoaster';
import { preloadEnquiryDialog, useEnquiryDialog } from '../enquiry/EnquiryProvider';
import usePricingTiers from './usePricingTiers';
import { PLAN_COPY, TIER_PHOTOS, paidTiers, planContextFor } from './pricingModel';

const NAMES = { standard: 'Standard', premium: 'Premium', sponsor: 'Venue Sponsor' };

/** "Best for" per tier (upgrade brief §3.3). */
export const BEST_FOR = {
  standard: 'Getting your name in front of locals.',
  premium: 'Telling your story and collecting leads.',
  sponsor: 'Owning one venue outright.',
};

/** The lowest live per-venue monthly price of the paid tiers, formatted; '' when unknown. */
export function fromPrice(tiers) {
  const prices = paidTiers(tiers).map((t) => Number(t.monthly_price_cents)).filter((n) => n > 0);
  return prices.length ? formatCents(Math.min(...prices)) : '';
}

/** One line of what a tier gets, from the live coaster figure (per month, per venue). */
export function teaserLine(tier) {
  const key = tier && tier.tier_key;
  const n = Number(tier && tier.coasters_per_venue) > 0 ? formatCount(tier.coasters_per_venue) : '';
  if (key === 'standard') return n ? `Your logo on ${n} shared coaster backs a month.` : 'Your logo on the shared coaster back.';
  if (key === 'premium') return n ? `Your own coaster front, ${n} a month, and a landing page.` : 'Your own coaster front and a landing page.';
  if (key === 'sponsor') return n ? `Both sides of ${n} coasters a month, the top banner and a mini website.` : 'Both sides of the coasters, the top banner and a mini website.';
  return '';
}

const TILE_TONES = {
  standard: 'border-brand-rule bg-brand-sheet text-navy',
  premium: 'border-brand-orange bg-brand-orange text-navy',
  sponsor: 'site-dark border-navy bg-navy text-brand-sheet',
};

/**
 * The compact pricing summary (owner upgrade brief 2026-09-27 §3.1 item 7) for pages that are
 * not /pricing: one tile per paid tier — name, "from $X/mo" (the live per-venue monthly price;
 * the published prices while the catalogue is unavailable), one line of what you get, and
 * "Choose {Tier}" (the enquiry dialog with that tier pre-selected). The calculator, billing
 * toggles and footnotes live on /pricing only. Each tile opens with its tier's coaster photo
 * (TierCoaster: the example coaster photo, "Example" tag, the example line and "View the design";
 * `photos="thumb"` a 104px tagged thumbnail beside the name; `photos={false}` none).
 * While the tiers load, nothing renders (no placeholder prices); on an error, nothing either:
 * the page's "See pricing" link remains.
 *
 * Props: `headingLevel` (3 default), `bestFor` (show the "Best for" line: For Businesses),
 * `photos`: 'full' (default: the photo across the top of the tile), 'thumb' (a small square
 * beside the name: Home's compact teaser) or false; `className`.
 */
export default function PricingTeaser({ headingLevel = 3, bestFor = false, photos = 'full', className }) {
  const { tiers, status } = usePricingTiers();
  const enquiry = useEnquiryDialog();
  const list = paidTiers(tiers);
  if (!(status === 'ready' || status === 'fallback') || !list.length) return null;
  return (
    <ul role="list" className={cn('m-0 grid list-none grid-cols-1 gap-4 p-0 min-[700px]:grid-cols-3', className)} data-testid="pricing-teaser">
      {list.map((tier) => {
        const key = tier.tier_key;
        const copy = PLAN_COPY[key] || {};
        const name = tier.name || NAMES[key] || key;
        const monthly = Number(tier.monthly_price_cents) > 0 ? formatCents(tier.monthly_price_cents) : '';
        return (
          <li key={key} className={cn('flex flex-col border p-5 shadow-[4px_5px_0] shadow-navy/10 nav:p-6', TILE_TONES[key])} data-plan={key}>
            {photos === 'full' ? (
              <TierCoaster
                tier={tier}
                sizes="(min-width: 1808px) 540px, (min-width: 700px) 32vw, 100vw"
                bleed="-mx-5 -mt-5 nav:-mx-6 nav:-mt-6"
              />
            ) : null}
            <div className={photos === 'thumb' ? 'flex items-center gap-4' : undefined}>
              {photos === 'thumb' && TIER_PHOTOS[key] ? (
                <div className="relative w-[104px] shrink-0">
                  <SiteImage id={TIER_PHOTOS[key]} ratio="1x1" tone="paper" sizes="104px" className="border border-navy/15" />
                  <span className="absolute bottom-1 left-1 bg-navy px-1.5 py-0.5 font-label text-[11px] uppercase text-brand-sheet">Example</span>
                </div>
              ) : null}
              <div className="min-w-0">
                <p className="m-0 font-label text-xs uppercase tracking-[0.04em]">{copy.label}</p>
                <Heading level={headingLevel} size="minor" className="mb-1 mt-2 text-[1.75rem] leading-[1.1]">{name}</Heading>
              </div>
            </div>
            {monthly ? (
              <p className="m-0 text-[0.9375rem]" data-testid="teaser-price">
                from <strong className="font-display text-[1.5rem] font-medium">{monthly}</strong>/mo per venue
              </p>
            ) : null}
            <p className={cn('mt-3 text-[0.9375rem] leading-[1.5]', bestFor ? 'mb-2' : 'mb-5')}>{teaserLine(tier)}</p>
            {bestFor && BEST_FOR[key] ? (
              <p className="mb-5 mt-0 text-[0.9375rem] leading-[1.5]" data-testid="teaser-best-for">
                <strong className="font-semibold">Best for:</strong> {BEST_FOR[key]}
              </p>
            ) : null}
            <Button
              size="sm"
              variant={key === 'sponsor' ? 'inverse' : 'primary'}
              className="mt-auto self-start"
              aria-haspopup="dialog"
              onPointerEnter={preloadEnquiryDialog}
              onFocus={preloadEnquiryDialog}
              onClick={() => enquiry.open({ role: 'advertiser', planContext: planContextFor(tier, 'quarterly', 1) })}
            >
              {copy.choose || `Choose ${name}`}
            </Button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * "Plans from $449/month per venue · or start with a free listing" under a hero's actions
 * (upgrade brief §3.3): the lowest live per-venue monthly price (published prices as the
 * fallback). Renders only the free-listing half while the price is unknown.
 */
export function PlansFromLine() {
  const { tiers, status } = usePricingTiers();
  const price = status === 'ready' || status === 'fallback' ? fromPrice(tiers) : '';
  return (
    <span data-testid="plans-from">
      {price ? `Plans from ${price}/month per venue · ` : ''}or start with a free listing
    </span>
  );
}
